import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import {
  SHOW_SEARCH_BAR,
  SIGNUP_TAB_DETAIL,
  SIGNUP_TAB_COMPLETED,
  BUSINESS_LISTING_TYPE_PUBLISHED,
  BUSINESS_LISTING_TYPE_DRAFT,
} from '../../../../util/types';
import { getBusinessListingParams } from '../../../../util/data';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  Button,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.topbarLink} name="InboxPage" params={{ tab: inboxTab }}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, intl }) => {

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    businessListing,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
  } = props;
  const [mounted, setMounted] = useState(false);

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };


  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      currentSearchParams={initialSearchFormValues}
      appConfig={config}
      currentPage={currentPage}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  const { emailVerified, profile } = (currentUser && currentUser.id && currentUser.attributes) || {};
  const { publicData, protectedData } = profile || {};
  const { isAdmin } = publicData || {};
  const {
    adminPanel,
    signUpCompleted,
    isSignupIncomplete = false,
    algoliaSearch = false,
    businessListingId = false,
    businessListingPublished = false,
  } = protectedData || {};

  const { pageCompleted } = (businessListing && businessListing.id && businessListing.attributes.privateData) || {};
  const editListingParams = getBusinessListingParams(currentUser);
  const showSearch = algoliaSearch && SHOW_SEARCH_BAR.includes(currentPage);
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const inboxLink = authenticatedOnClientSide
    ? isSignupIncomplete
      ? <NamedLink className={css.navLink} name="SignUpStepsPage" params={{ tab: "detail" }} >
        <span className={css.createListing}>
          <FormattedMessage id={signUpCompleted == SIGNUP_TAB_DETAIL
            ? 'TopbarDesktop.profileDetail'
            : 'TopbarDesktop.profileReference'} />
        </span>
      </NamedLink>
      : signUpCompleted && signUpCompleted == SIGNUP_TAB_COMPLETED
        ? emailVerified
          ? businessListingId
            ? businessListingPublished == BUSINESS_LISTING_TYPE_PUBLISHED
              ?
              <>
                <NamedLink
                  className={css.rfpOwnerLink}
                  name="DashboardBasePage"
                  params={{ tab: "owner" }}
                >
                  <span className={css.rfpText}>
                    <FormattedMessage id="TopbarDesktop.rfpOwner" /> {notificationDot}
                  </span>
                </NamedLink>
                <NamedLink
                  className={css.rfpLink}
                  name="ProviderDashBoardPage"
                  params={{ tab: "provider" }}
                >
                  <span className={css.rfpText}>
                    <FormattedMessage id="TopbarDesktop.rfpProvider" /> {notificationDot}
                  </span>
                </NamedLink>
                {isAdmin || adminPanel
                  ? <NamedLink
                    className={css.rfpOwnerLink}
                    name="AdminDashboardBasePage"
                  >
                    <span className={css.rfpText}>
                      <FormattedMessage id="TopbarDesktop.adminDashboardPage" /> {notificationDot}
                    </span>
                  </NamedLink>
                  : null}
              </>
              : pageCompleted == 'details'
                ? <NamedLink className={css.navLink} name="EditListingPage" params={editListingParams}>
                  <span className={css.createListing}>
                    <FormattedMessage id={"TopbarDesktop.businessDetail"} />
                  </span>
                </NamedLink>
                : pageCompleted == 'finalStep'
                  ? <NamedLink className={css.navLink} name="EditListingPage" params={editListingParams}>
                    <span className={css.createListing}>
                      <FormattedMessage id={"TopbarDesktop.businessFinal"} />
                    </span>
                  </NamedLink>
                  : <NamedLink className={css.navLink} name="StripePayoutPage" >
                    <span className={css.createListing}>
                      <FormattedMessage id={"TopbarDesktop.addBankDetails"} />
                    </span>
                  </NamedLink>
            : null
          : <Button
            className={css.buttonLink}
            onClick={onResendVerificationEmail}
            inProgress={sendVerificationEmailInProgress}
          >
            <span className={css.createListing}>
              <FormattedMessage id={"TopbarDesktop.verifyEmail"} />
            </span>
          </Button>
        : null
    : null;

  const createEvent = ((businessListingId && businessListingPublished == BUSINESS_LISTING_TYPE_PUBLISHED)
    ? <NamedLink className={css.navLink} name="NewListingPage">
      <span className={css.createListing}>
        <FormattedMessage id="TopbarDesktop.createRFP" />
      </span>
    </NamedLink>
    : null);

  const menuItems = [];

  if (isAdmin || adminPanel) {
    menuItems.push({
      key: "AdminDashboardPage",
      id: "TopbarDesktop.adminDashboardLink",
      css: classNames(css.yourListingsLink, currentPageClass('AdminDashboardPage')),
      pageCss: "AdminDashboardPage",
      type: "NamedLink",
      name: "AdminDashboardBasePage",
    });
  }

  // if (!businessListingId || isSignupIncomplete) {
  //   menuItems.push({
  //     key: "EditListingPage",
  //     id: "TopbarDesktop.myBusiness",
  //     css: classNames(css.yourListingsLink, currentPageClass('EditListingPage')),
  //     pageCss: "EditListingPage",
  //     type: "NamedLink",
  //     name: (businessListingId ? "EditListingPage" : "SignUpStepsPage"),
  //     params: (businessListingId ? editListingParams : { tab: "detail" })
  //   });
  // }


  menuItems.push(...[
    {
      key: "AccountSettingsPage",
      id: "TopbarDesktop.accountSettingsLink",
      css: classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage')),
      pageCss: "AccountSettingsPage",
      type: "NamedLink",
      name: "AccountSettingsPage",
    },
    {
      key: "ManageListingsPage",
      id: "TopbarDesktop.manageListings",
      css: classNames(css.yourListingsLink, currentPageClass('ManageListingsPage')),
      pageCss: "ManageListingsPage",
      type: "NamedLink",
      name: "ManageListingsPage",
    },
    {
      key: "logout",
      id: "TopbarDesktop.logout",
      css: classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage')),
      type: "Button",
      fn: onLogout,
    }]);

  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {menuItems.map(m => (<MenuItem key={m.key}>
          {m.type == "NamedLink"
            ? <NamedLink
              className={classNames(m.css, currentPageClass(m.pageCss))}
              name={m.name}
              params={m.params || {}}
            >
              {m.key == 'InboxPage' && notificationDot
                ? <span className={css.inbox}>
                  {notificationDot}
                </span>
                : <span className={css.menuItemBorder} />}
              <FormattedMessage id={m.id} />
            </NamedLink>
            : m.type == "Button"
              ? <InlineTextButton rootClassName={css.logoutButton} onClick={m.fn}>
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.logout" />
              </InlineTextButton>
              : null}
        </MenuItem>))}
      </MenuContent>
    </Menu>
  ) : null;

  const listMyBusinessLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.listMyBusinessLink}>
      <span className={css.listMyBusiness}>
        List My Business or Event
      </span>
    </NamedLink>
  );

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      {searchFormMaybe}
      {/* {showSearch ? search : <div className={css.searchLink} />} */}

      {createEvent}
      {inboxLink}
      {profileMenu}
      {listMyBusinessLink}
      {signupLink}
      {loginLink}
    </nav>
  );
};

export default TopbarDesktop;